// src/pages/GalleryPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaHome, 
  FaChevronRight, 
  FaFilter, 
  FaTimes, 
  FaSearch,
  FaImage,
  FaVideo,
  FaExpand,
  FaPlay,
  FaTags,
  FaCalendarAlt,
  FaCheck,
  FaChevronLeft,
} from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';

const GalleryPage = () => {
  // State for filtering and modals
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeYear, setActiveYear] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [viewType, setViewType] = useState('grid'); // 'grid' or 'masonry'
  
  // Mock gallery data (in a real app, this would come from an API)
  const galleryData = [
    {
      id: 1,
      title: "Cohort 12 Certification Ceremony",
      description: "Graduates receiving their certificates at the Cohort 12 Certification Ceremony held at Fourah Bay College",
      type: "image",
      url: "/images/gallery/certification-ceremony-1.jpg",
      thumbnail: "/images/gallery/thumbnails/certification-ceremony-1.jpg",
      category: "certification",
      tags: ["certification", "graduates", "ceremony"],
      date: "2024-12-15",
      year: "2024"
    },
    {
      id: 2,
      title: "AutoCAD Training Session",
      description: "Students learning AutoCAD during a training session at the Afrovate Tech Hub Training Center",
      type: "image",
      url: "/images/gallery/training-session-1.jpg",
      thumbnail: "/images/gallery/thumbnails/training-session-1.jpg",
      category: "training",
      tags: ["training", "autocad", "students"],
      date: "2024-10-05",
      year: "2024"
    },
    {
      id: 3,
      title: "Field Trip to GiftMed Construction Site",
      description: "Students observing construction techniques during a field trip to the GiftMed site",
      type: "image",
      url: "/images/gallery/field-trip-1.jpg",
      thumbnail: "/images/gallery/thumbnails/field-trip-1.jpg",
      category: "field-trip",
      tags: ["field-trip", "construction", "practical-learning"],
      date: "2024-11-20",
      year: "2024"
    },
    {
      id: 4,
      title: "Career Talk with Industry Professionals",
      description: "Panel discussion featuring industry professionals sharing insights with Afrovate Tech Hub students",
      type: "image",
      url: "/images/gallery/career-talk-1.jpg",
      thumbnail: "/images/gallery/thumbnails/career-talk-1.jpg",
      category: "career-talk",
      tags: ["career-talk", "panel", "professionals"],
      date: "2024-09-18",
      year: "2024"
    },
    {
      id: 5,
      title: "Revit Architecture Workshop",
      description: "Hands-on workshop teaching Revit Architecture to Cohort 11 students",
      type: "image",
      url: "/images/gallery/workshop-1.jpg",
      thumbnail: "/images/gallery/thumbnails/workshop-1.jpg",
      category: "workshop",
      tags: ["workshop", "revit", "architecture"],
      date: "2024-08-12",
      year: "2024"
    },
    {
      id: 6,
      title: "Drone Technology Introduction",
      description: "Students learning about drone technology for surveying applications",
      type: "video",
      url: "https://www.youtube.com/embed/TXH8wiHE0dM",
      thumbnail: "/images/gallery/thumbnails/drone-video-1.jpg",
      category: "workshop",
      tags: ["drone", "surveying", "technology"],
      date: "2024-07-22",
      year: "2024"
    },
    {
      id: 7,
      title: "Cohort 10 Graduation",
      description: "Celebration of Cohort 10 graduates at the certification ceremony",
      type: "image",
      url: "/images/gallery/graduation-1.jpg",
      thumbnail: "/images/gallery/thumbnails/graduation-1.jpg",
      category: "certification",
      tags: ["graduation", "certification", "celebration"],
      date: "2023-12-10",
      year: "2023"
    },
    {
      id: 8,
      title: "Visit from Engineers Without Borders",
      description: "Engineers Without Borders representatives visiting Afrovate Tech Hub training facilities",
      type: "image",
      url: "/images/gallery/visitors-1.jpg",
      thumbnail: "/images/gallery/thumbnails/visitors-1.jpg",
      category: "partners",
      tags: ["partners", "international", "collaboration"],
      date: "2023-11-05",
      year: "2023"
    },
    {
      id: 9,
      title: "Computer Programming Class",
      description: "Students learning Python programming during a training session",
      type: "image",
      url: "/images/gallery/programming-1.jpg",
      thumbnail: "/images/gallery/thumbnails/programming-1.jpg",
      category: "training",
      tags: ["programming", "python", "coding"],
      date: "2023-10-15",
      year: "2023"
    },
    {
      id: 10,
      title: "National Youth Employment Fair Participation",
      description: "Afrovate Tech Hub booth at the National Youth Employment and Entrepreneurship Fair",
      type: "image",
      url: "/images/gallery/exhibition-1.jpg",
      thumbnail: "/images/gallery/thumbnails/exhibition-1.jpg",
      category: "exhibition",
      tags: ["exhibition", "entrepreneurship", "youth"],
      date: "2023-08-15",
      year: "2023"
    },
    {
      id: 11,
      title: "Climate Conference Presentation",
      description: "Afrovate Tech Hub CEO presenting at the Climate Conference in Freetown",
      type: "video",
      url: "https://www.youtube.com/embed/TXH8wiHE0dM",
      thumbnail: "/images/gallery/thumbnails/climate-video-1.jpg",
      category: "conference",
      tags: ["climate", "presentation", "sustainability"],
      date: "2023-06-28",
      year: "2023"
    },
    {
      id: 12,
      title: "BIM Training Session",
      description: "Students learning Building Information Modeling software techniques",
      type: "image",
      url: "/images/gallery/bim-training-1.jpg",
      thumbnail: "/images/gallery/thumbnails/bim-training-1.jpg",
      category: "training",
      tags: ["bim", "software", "design"],
      date: "2023-05-20",
      year: "2023"
    },
    {
      id: 13,
      title: "Cohort 8 Certification",
      description: "Cohort 8 graduates with guest speakers at the certification ceremony",
      type: "image",
      url: "/images/gallery/cohort8-1.jpg",
      thumbnail: "/images/gallery/thumbnails/cohort8-1.jpg",
      category: "certification",
      tags: ["certification", "graduates", "achievement"],
      date: "2022-12-18",
      year: "2022"
    },
    {
      id: 14,
      title: "Fourah Bay College Collaboration",
      description: "Signing ceremony for collaboration between Afrovate Tech Hub and Fourah Bay College",
      type: "image",
      url: "/images/gallery/collaboration-1.jpg",
      thumbnail: "/images/gallery/thumbnails/collaboration-1.jpg",
      category: "partners",
      tags: ["collaboration", "academic", "partnership"],
      date: "2022-11-05",
      year: "2022"
    },
    {
      id: 15,
      title: "Land Surveying Workshop",
      description: "Practical land surveying workshop with industry equipment",
      type: "image",
      url: "/images/gallery/surveying-1.jpg",
      thumbnail: "/images/gallery/thumbnails/surveying-1.jpg",
      category: "workshop",
      tags: ["surveying", "practical", "geospatial"],
      date: "2022-09-10",
      year: "2022"
    }
  ];

  // Extract unique years from gallery data
  const years = ['all', ...new Set(galleryData.map(item => item.year))];
  
  // Gallery categories
  const categories = [
    { id: 'all', name: 'All Items' },
    { id: 'certification', name: 'Certification Ceremonies' },
    { id: 'training', name: 'Training Sessions' },
    { id: 'field-trip', name: 'Field Trips' },
    { id: 'career-talk', name: 'Career Talks' },
    { id: 'workshop', name: 'Workshops' },
    { id: 'partners', name: 'Partners & Collaborations' },
    { id: 'exhibition', name: 'Exhibitions & Fairs' },
    { id: 'conference', name: 'Conferences' }
  ];
  
  // Filter gallery items based on category, year, and search term
  useEffect(() => {
    const filtered = galleryData.filter(item => {
      const matchesCategory = activeFilter === 'all' || item.category === activeFilter;
      const matchesYear = activeYear === 'all' || item.year === activeYear;
      const matchesSearch = 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchesCategory && matchesYear && matchesSearch;
    });
    
    setFilteredItems(filtered);
  }, [activeFilter, activeYear, searchTerm]);
  
  // Effect to scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Open lightbox with selected image
  const openLightbox = (item) => {
    setCurrentImage(item);
    setLightboxOpen(true);
    // Prevent scrolling when lightbox is open
    document.body.style.overflow = 'hidden';
  };
  
  // Close lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    // Restore scrolling
    document.body.style.overflow = 'auto';
  };
  
  // Navigate to next/previous image in lightbox
  const navigateImage = (direction) => {
    if (!currentImage) return;
    
    const currentIndex = filteredItems.findIndex(item => item.id === currentImage.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % filteredItems.length;
    } else {
      newIndex = (currentIndex - 1 + filteredItems.length) % filteredItems.length;
    }
    
    setCurrentImage(filteredItems[newIndex]);
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  
  return (
    <>
      <Helmet>
        <title>Gallery - Afrovate Tech Hub</title>
        <meta name="description" content="Browse photos and videos from Afrovate Tech Hub's events, training sessions, and activities. See our impact in action." />
      </Helmet>
      
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-16 overflow-hidden"
        style={{
          backgroundImage: 'url(/images/gallery/gallery-hero-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-75"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">Gallery</span>
          </div>
          
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
            >
              Photo & Video Gallery
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-xl text-white text-opacity-90 mb-8"
            >
              Browse our collection of photos and videos showcasing Afrovate Tech Hub's activities, 
              events, and achievements over the years.
            </motion.p>
            
            {/* Search Bar */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="relative max-w-xl mx-auto"
            >
              <input
                type="text"
                placeholder="Search gallery..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-3 px-6 pr-12 bg-white bg-opacity-95 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-secondary"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                {searchTerm ? (
                  <button onClick={() => setSearchTerm('')} className="hover:text-primary">
                    <FaTimes />
                  </button>
                ) : (
                  <FaSearch />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Gallery Filtering */}
      <div className="bg-white border-t border-b border-gray-200 sticky top-16 z-20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Category Filter */}
            <div className="flex items-center">
              <div className="flex items-center text-gray-700 mr-3">
                <FaFilter className="mr-2" /> Category:
              </div>
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-700 py-2 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Year Filter */}
            <div className="flex items-center">
              <div className="flex items-center text-gray-700 mr-3">
                <FaCalendarAlt className="mr-2" /> Year:
              </div>
              <select
                value={activeYear}
                onChange={(e) => setActiveYear(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-700 py-2 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year === 'all' ? 'All Years' : year}
                  </option>
                ))}
              </select>
            </div>
            
            {/* View Type Toggle */}
            <div className="flex items-center">
              <div className="flex items-center text-gray-700 mr-3">
                <FaImage className="mr-2" /> View:
              </div>
              <div className="bg-gray-100 rounded-full p-1 inline-flex">
                <button
                  className={`px-3 py-1 rounded-full font-medium text-sm transition-colors ${
                    viewType === 'grid'
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setViewType('grid')}
                >
                  Grid
                </button>
                <button
                  className={`px-3 py-1 rounded-full font-medium text-sm transition-colors ${
                    viewType === 'masonry'
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setViewType('masonry')}
                >
                  Masonry
                </button>
              </div>
            </div>
            
            {/* Clear Filters */}
            {(activeFilter !== 'all' || activeYear !== 'all' || searchTerm) && (
              <button
                onClick={() => {
                  setActiveFilter('all');
                  setActiveYear('all');
                  setSearchTerm('');
                }}
                className="text-primary font-medium hover:text-primary-dark flex items-center"
              >
                <FaTimes className="mr-2" /> Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Gallery Grid */}
      <Section background="gray" padding="lg">
        <div className="mb-8">
          <Heading level={2}>
            Gallery
          </Heading>
          <p className="text-gray-600">
            {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} found
            {searchTerm && ` matching "${searchTerm}"`}
            {activeFilter !== 'all' && ` in ${categories.find(c => c.id === activeFilter)?.name}`}
            {activeYear !== 'all' && ` from ${activeYear}`}
          </p>
        </div>
        
        {filteredItems.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <div className="text-gray-400 text-5xl mb-4">
              <FaImage className="mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Gallery Items Found</h3>
            <p className="text-gray-600 mb-6">
              We couldn't find any photos or videos matching your criteria.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setActiveFilter('all');
                setActiveYear('all');
                setSearchTerm('');
              }}
            >
              View All Gallery Items
            </Button>
          </div>
        ) : (
          <motion.div
            className={`${
              viewType === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6'
            }`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredItems.map((item) => (
              <motion.div
                key={item.id}
                variants={itemVariants}
                className={`${
                  viewType === 'grid'
                    ? ''
                    : 'break-inside-avoid'
                } bg-white rounded-lg shadow-md overflow-hidden transform transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-pointer`}
                onClick={() => item.type === 'image' && openLightbox(item)}
              >
                <div className="relative">
                  {/* Thumbnail */}
                  <img
                    src={item.thumbnail}
                    alt={item.title}
                    className="w-full h-60 object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://placehold.co/400x300/1a237e/ffffff?text=${item.title.replace(/ /g, '+')}`;
                    }}
                  />
                  
                  {/* Video Play Button Overlay */}
                  {item.type === 'video' && (
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white bg-opacity-80 text-primary p-4 rounded-full hover:bg-opacity-100 transition-all"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaPlay className="h-6 w-6" />
                      </a>
                    </div>
                  )}
                  
                  {/* Image Expand Button */}
                  {item.type === 'image' && (
                    <div className="absolute top-2 right-2">
                      <button
                        className="bg-white bg-opacity-80 text-primary p-2 rounded-full hover:bg-opacity-100 transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          openLightbox(item);
                        }}
                      >
                        <FaExpand className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Category Badge */}
                  <div className="absolute top-2 left-2">
                    <span className="inline-block bg-primary bg-opacity-80 text-white text-xs font-medium rounded-full px-2 py-1">
                      {categories.find(c => c.id === item.category)?.name || item.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-4">
                  <h3 className="font-bold text-primary-dark mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 items-center">
                    <FaTags className="text-gray-400 text-xs" />
                    {item.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  {/* Date */}
                  <div className="text-xs text-gray-500 mt-3">
                    {new Date(item.date).toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </Section>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && currentImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              className="absolute top-6 right-6 text-white hover:text-secondary text-3xl"
              onClick={closeLightbox}
            >
              <FaTimes />
            </button>
            
            {/* Navigation buttons */}
            <button
              className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white hover:text-secondary text-3xl"
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('prev');
              }}
            >
              <FaChevronLeft />
            </button>
            
            <button
              className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white hover:text-secondary text-3xl"
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('next');
              }}
            >
              <FaChevronRight />
            </button>
            
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="max-w-4xl max-h-[80vh] p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={currentImage.url}
                alt={currentImage.title}
                className="max-w-full max-h-[70vh] object-contain mx-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://placehold.co/800x600/1a237e/ffffff?text=${currentImage.title.replace(/ /g, '+')}`;
                }}
              />
              
              {/* Image caption */}
              <div className="text-white mt-4 text-center">
                <h3 className="text-xl font-bold mb-2">{currentImage.title}</h3>
                <p className="text-gray-300">{currentImage.description}</p>
                <p className="text-gray-400 text-sm mt-2">
                  {new Date(currentImage.date).toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Upload Section */}
      <Section background="white" padding="lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <Heading level={2} className="mb-6">
              Share Your Photos & Videos
            </Heading>
            <p className="text-gray-700 mb-4">
              Were you part of an Afrovate Tech Hub event or training? We'd love to see your photos and videos! 
              Share your memories with us, and they might be featured in our gallery.
            </p>
            <p className="text-gray-700 mb-6">
              Contact us with your submissions, ensuring you include details about the event, date, 
              and your consent for us to publish the content.
            </p>
            <Button 
              variant="primary" 
              size="lg" 
              to="/contact" 
            >
              Submit Your Media
            </Button>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-gray-50 p-6 rounded-lg shadow-md"
          >
            <h3 className="text-xl font-bold text-primary-dark mb-4">Submission Guidelines</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <FaCheck className="text-green-600 text-xs" />
                </div>
                <span className="text-gray-700">High-resolution photos (minimum 1200px width)</span>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <FaCheck className="text-green-600 text-xs" />
                </div>
                <span className="text-gray-700">Videos should be uploaded to YouTube with a public or unlisted link</span>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <FaCheck className="text-green-600 text-xs" />
                </div>
                <span className="text-gray-700">Include event name, date, and location with your submission</span>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <FaCheck className="text-green-600 text-xs" />
                </div>
                <span className="text-gray-700">Written consent for Afrovate Tech Hub to publish your content</span>
              </li>
              <li className="flex items-start">
                <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <FaCheck className="text-green-600 text-xs" />
                </div>
                <span className="text-gray-700">Content must be related to Afrovate Tech Hub events or activities</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </Section>
      
      {/* Video Gallery Section */}
      <Section background="gray" padding="lg">
        <div className="text-center mb-12">
          <Heading level={2} align="center" className="mb-4" withAccent>
            Video Gallery
          </Heading>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Watch videos from our events, training sessions, and activities
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {galleryData.filter(item => item.type === 'video').slice(0, 3).map((video) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div className="relative aspect-video">
                <iframe
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-primary-dark mb-2">{video.title}</h3>
                <p className="text-gray-600 text-sm">{video.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="text-center mt-8">
          <Button 
            variant="primary" 
            to="https://www.youtube.com/@afrovatetechhub" 
            target="_blank"
          >
            Visit Our YouTube Channel
          </Button>
        </div>
      </Section>
      
      {/* Call to Action */}
      <Section background="gradient-primary" padding="lg">
        <div className="text-center max-w-3xl mx-auto">
          <Heading level={2} color="white" align="center" className="mb-6">
            Join Our Next Event
          </Heading>
          <p className="text-xl text-white text-opacity-90 mb-10">
            Be part of our growing community! Attend our upcoming events to enhance your skills,
            network with professionals, and explore new opportunities.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Button 
              variant="secondary" 
              size="lg" 
              to="/events"
            >
              View Upcoming Events
            </Button>
            <Button 
              variant="light" 
              size="lg" 
              to="/programs"
            >
              Explore Our Programs
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};

export default GalleryPage;